import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { taskApi, customerApi, userApi, projectApi } from '../api/endpoints.js';
import { useApi } from '../hooks/useApi.js';
import { useToast } from '../hooks/useToast.jsx';
import { useApiError } from '../hooks/useApiError.js';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import Toolbar from '../components/Toolbar.jsx';
import Badge from '../components/Badge.jsx';
import Modal from '../components/Modal.jsx';
import Spinner from '../components/Spinner.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { Field, Select, TextInput, TextArea } from '../components/Field.jsx';
import { formatDateTime, fullName } from '../utils/format.js';
import { TASK_PRIORITIES, DELIVERY_ROLES, worksAlone } from '../constants.js';
import CommentThread from '../components/CommentThread.jsx';
import { useAuth } from '../auth/AuthContext.jsx';

const EMPTY = {
  title: '',
  description: '',
  customerId: '',
  projectId: '',
  assignedTo: '',
  dueAt: '',
  priority: 'NORMAL',
  isPrivate: false,
};

/** Follow-ups: the "call them back on Thursday" layer of the CRM. */
const TasksPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const toMessage = useApiError();
  const locale = i18n.resolvedLanguage;

  // A telepro's tasks are their own; the API forces it, the form does not ask.
  const canDelegate = !worksAlone(user.role);
  // Only the delivery roles hold PROJECT_READ, so only they are asked to which
  // piece of work a follow-up belongs - for everybody else the field would be
  // an empty dropdown backed by a 403.
  const hasProjects = DELIVERY_ROLES.includes(user.role);

  const [filter, setFilter] = useState('OPEN');
  // Yours by default. This is a personal to-do list first; the team's follow-ups
  // are one click away, not the thing you are shown on arrival.
  const [mine, setMine] = useState(true);
  const [form, setForm] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, loading, refresh } = useApi(
    () => taskApi.list({ status: filter || undefined, mine: mine || undefined }),
    [filter, mine],
  );
  const { data: customers } = useApi(() => customerApi.list({ pageSize: 200 }), []);
  const { data: directory } = useApi(() => (canDelegate ? userApi.directory() : Promise.resolve(null)), []);
  // Open projects only: a finished one is not somewhere new work is filed.
  const { data: projects } = useApi(
    () => (hasProjects ? projectApi.list({ open: 'yes', pageSize: 200 }) : Promise.resolve(null)),
    [],
  );

  // Opened from a notification: reopen that task so its thread is on screen.
  // The filter is cleared first, or a task already marked done would not be in
  // the list to reopen.
  useEffect(() => {
    const wanted = searchParams.get('task');
    if (!wanted || form?.id === wanted) return;
    const task = (data?.items ?? []).find((row) => row.id === wanted);
    if (task) setForm({ ...task, projectId: task.projectId ?? '', dueAt: task.dueAt?.slice(0, 16) ?? '' });
    // Widen the filters before giving up: a task somebody else assigned, or one
    // already ticked off, is not in the default view but is still openable.
    else if (mine) setMine(false);
    else if (filter) setFilter('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data]);

  const save = async () => {
    try {
      const body = {
        title: form.title,
        description: form.description,
        priority: form.priority,
        customerId: form.customerId || null,
        ...(hasProjects ? { projectId: form.projectId || null } : {}),
        ...(canDelegate ? { assignedTo: form.assignedTo || null } : {}),
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : null,
        isPrivate: Boolean(form.isPrivate),
      };
      if (form.id) await taskApi.update(form.id, body);
      else await taskApi.create(body);
      toast.success(t('common.save'));
      setForm(null);
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const setStatus = async (task, status) => {
    try {
      await taskApi.update(task.id, { status });
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  if (loading) return <Spinner full />;
  const rows = data?.items ?? [];

  return (
    <>
      <PageHeader
        title={t('tasks.title')}
        subtitle={t('tasks.subtitle')}
        actions={
          <button type="button" className="btn btn--primary" onClick={() => setForm({ ...EMPTY })}>
            {t('tasks.newTask')}
          </button>
        }
      />

      <Toolbar>
        <div className="segmented" role="group">
          {['OPEN', 'DONE', ''].map((status) => (
            <button
              key={status || 'all'}
              type="button"
              className={filter === status ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
              onClick={() => setFilter(status)}
            >
              {status ? t(`tasks.statuses.${status}`) : t('common.all')}
            </button>
          ))}
        </div>
        <label className={mine ? 'option option--checked' : 'option'}>
          <input type="checkbox" checked={mine} onChange={() => setMine(!mine)} />
          <span>{t('tasks.mine')}</span>
        </label>
        <span className="toolbar__count">
          {rows.length} {t('common.results')}
        </span>
      </Toolbar>

      <Card>
        {rows.length ? (
          <ul className="task-list">
            {rows.map((task) => (
              <li key={task.id} className={task.overdue ? 'task task--overdue' : 'task'}>
                <input
                  type="checkbox"
                  checked={task.status === 'DONE'}
                  onChange={() => setStatus(task, task.status === 'DONE' ? 'OPEN' : 'DONE')}
                  aria-label={t('tasks.statuses.DONE')}
                />
                <div className="task__body">
                  <button
                    type="button"
                    className="task__title"
                    onClick={() =>
                      setForm({ ...task, projectId: task.projectId ?? '', dueAt: task.dueAt?.slice(0, 16) ?? '' })
                    }
                  >
                    {task.title}
                  </button>
                  <span className="task__meta">
                    {/* Back to the piece of work this belongs to - the spec and
                        the sibling tasks are there, not here. */}
                    {task.projectId && task.projectTitle ? (
                      <>
                        <Link
                          to={`/it/projects?project=${task.projectId}`}
                          className="cell-link"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {task.projectTitle}
                        </Link>
                        {' - '}
                      </>
                    ) : null}
                    {task.customerName ? `${task.customerName} - ` : ''}
                    {task.assignee ? fullName(task.assignee) : ''}
                    {task.dueAt ? ` - ${formatDateTime(task.dueAt, locale)}` : ''}
                  </span>
                </div>
                <Badge
                  value={task.priority === 'HIGH' ? 'HOT' : task.priority === 'LOW' ? 'COLD' : 'WARM'}
                  label={t(`tasks.priorities.${task.priority}`)}
                />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title={t('tasks.empty')} />
        )}
      </Card>

      <Modal
        open={Boolean(form)}
        title={form?.id ? t('common.edit') : t('tasks.newTask')}
        onClose={() => {
          setForm(null);
          if (searchParams.get('task')) setSearchParams({}, { replace: true });
        }}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setForm(null)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--primary" onClick={save}>
              {t('common.save')}
            </button>
          </>
        }
      >
        {form ? (
          <div className="form-grid">
            <div className="span-2">
              <Field label={t('tasks.taskTitle')} htmlFor="t-title" required>
                <TextInput id="t-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
              </Field>
            </div>
            <Field label={t('customers.companyName')} htmlFor="t-customer">
              <Select
                id="t-customer"
                value={form.customerId ?? ''}
                onChange={(v) => setForm({ ...form, customerId: v })}
                placeholder={t('common.none')}
                options={(customers?.items ?? []).map((c) => ({ value: c.id, label: c.companyName }))}
              />
            </Field>
            {hasProjects ? (
              <Field label={t('projects.project')} htmlFor="t-project">
                <Select
                  id="t-project"
                  value={form.projectId ?? ''}
                  onChange={(v) => {
                    // Filing a task under a project also files it under that
                    // project's account - they cannot disagree.
                    const project = (projects?.items ?? []).find((row) => row.id === v);
                    setForm({
                      ...form,
                      projectId: v,
                      customerId: project ? project.customerId : form.customerId,
                    });
                  }}
                  placeholder={t('common.none')}
                  options={(projects?.items ?? []).map((project) => ({
                    value: project.id,
                    label: project.customerName ? `${project.title} - ${project.customerName}` : project.title,
                  }))}
                />
              </Field>
            ) : null}
            {canDelegate ? (
              <Field label={t('tasks.assignedTo')} htmlFor="t-assignee">
                <Select
                  id="t-assignee"
                  value={form.assignedTo ?? ''}
                  onChange={(v) => setForm({ ...form, assignedTo: v })}
                  placeholder={t('common.none')}
                  options={(directory?.items ?? []).map((u) => ({ value: u.id, label: fullName(u) }))}
                />
              </Field>
            ) : (
              <Field label={t('tasks.assignedTo')}>
                <p className="static-value">{t('tasks.assignedToYou')}</p>
              </Field>
            )}
            <Field label={t('tasks.dueAt')} htmlFor="t-due">
              <TextInput
                id="t-due"
                type="datetime-local"
                value={form.dueAt ?? ''}
                onChange={(v) => setForm({ ...form, dueAt: v })}
              />
            </Field>
            <Field label={t('tasks.priority')} htmlFor="t-prio">
              <Select
                id="t-prio"
                value={form.priority}
                onChange={(v) => setForm({ ...form, priority: v })}
                options={TASK_PRIORITIES.map((p) => ({ value: p, label: t(`tasks.priorities.${p}`) }))}
              />
            </Field>
            {/* A private task can only be your own - assigning it away would
                hide it from the person meant to do it. */}
            <div className="span-2">
              <label className={form.isPrivate ? 'option option--checked' : 'option'}>
                <input
                  type="checkbox"
                  checked={Boolean(form.isPrivate)}
                  onChange={() =>
                    setForm({ ...form, isPrivate: !form.isPrivate, ...(!form.isPrivate ? { assignedTo: '' } : {}) })
                  }
                />
                <span>{t('tasks.private')}</span>
              </label>
              <p className="muted field__hint">{t('tasks.privateHint')}</p>
            </div>

            <div className="span-2">
              <Field label={t('common.notes')} htmlFor="t-desc">
                <TextArea id="t-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
              </Field>
            </div>

            {/* Only an existing task can hold a thread - there is nothing to
                attach a comment to until it has been saved once. */}
            {form.id ? (
              <div className="span-2">
                <CommentThread
                  taskId={form.id}
                  title={t('comments.onTask')}
                  highlightId={searchParams.get('comment')}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default TasksPage;
